import { useState, useEffect, useRef } from 'react';

interface UseCountUpProps {
  end: number;
  duration?: number;
  startOnMount?: boolean;
  decimalPlaces?: number;
  /**
   * Startet die Zaehl-Animation neu, sobald sich der Wert aendert – auch wenn
   * `end` gleich bleibt (z. B. nach einem manuellen Dashboard-Refresh).
   */
  restartKey?: number;
}

// Sanfteres Easing: beschleunigt das Finale, damit die letzten Werte flotter erscheinen
const easeOutCubic = (t: number): number => {
  return 1 - Math.pow(1 - t, 3);
};

const useCountUp = ({
  end,
  duration = 2000,
  startOnMount = true,
  decimalPlaces = 0,
  restartKey = 0,
}: UseCountUpProps): string => {
  const [count, setCount] = useState(0);
  const frameRate = 1000 / 60; // 60 FPS
  const totalFrames = Math.max(Math.round(duration / frameRate), 1);
  const animationFrameId = useRef<number | null>(null);
  const isMounted = useRef(true);
  const hasStarted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!startOnMount) {
      return;
    }

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    let frame = 0;
    hasStarted.current = true;
    setCount(0);

    const counter = () => {
      frame++;
      const progress = easeOutCubic(Math.min(frame / totalFrames, 1));
      const currentNum = progress * end;

      if (isMounted.current) {
        setCount(currentNum);
      }

      if (frame < totalFrames && isMounted.current) {
        animationFrameId.current = requestAnimationFrame(counter);
      } else {
        if (isMounted.current) {
          setCount(end);
        }
        hasStarted.current = false;
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }
      }
    };

    animationFrameId.current = requestAnimationFrame(counter);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      hasStarted.current = false;
    };
  }, [end, duration, startOnMount, totalFrames, restartKey]);

  return count.toFixed(decimalPlaces);
};

export default useCountUp; 